import { createContext, useCallback, useContext, useEffect, useReducer, type ReactNode } from 'react';
import type {
  CacheState,
  OverlayState,
  StoreShape,
  WorklogEvent,
  WorklogState,
} from '../worklog/types';
import { ALL_EVENTS } from '../worklog/types';
import { useStatus } from './StatusContext';

const STORAGE_PREFIX = 'worklog::';
// The contract pin lives in sessionStorage, NOT localStorage, and that choice
// is load-bearing. A localStorage pin outlives the week it names: once "week
// 11" was stored it was re-read on every load forever, still resolved against
// snapshot.contracts (where week 11 remains as history), and so the dashboard
// kept rendering a finished week while the CLI had moved to week 12. Nothing
// revalidated it. A sessionStorage pin cannot outlive the tab, so every fresh
// load falls back to whatever the snapshot says is current. Staleness here is
// bounded by construction rather than by remembering to invalidate.
const ACTIVE_CONTRACT_KEY = STORAGE_PREFIX + '@active-contract';
// Empty string = "follow whatever the snapshot says is current".
const FOLLOW_LIVE = '';

function storageKey(contract: string) {
  return STORAGE_PREFIX + contract;
}

function loadStore(contract: string): StoreShape {
  try {
    const raw = localStorage.getItem(storageKey(contract));
    if (!raw) return { state: {}, comments: {} };
    const parsed = JSON.parse(raw);
    return { state: parsed.state || {}, comments: parsed.comments || {} };
  } catch {
    return { state: {}, comments: {} };
  }
}

function saveStore(contract: string, store: StoreShape) {
  try {
    localStorage.setItem(storageKey(contract), JSON.stringify(store));
  } catch {
    // unavailable
  }
}

interface State {
  activeContract: string;
  worklog: WorklogState;
  overlay: OverlayState;
  store: StoreShape;
}

// Items and todos now flow exclusively through StatusContext (snapshot.items /
// snapshot.todos read from D1 via /api/work/snapshot). The legacy /api/items
// path was a parallel GitHub-raw fetch that drifted from D1; removed per
// audit 2026-05-22T062448Z reconciliation Pair 4/5 (LEGACY_DUAL_PATH).
type Action =
  | { type: 'contract/set'; contract: string }
  | { type: 'worklog/loading' }
  | { type: 'worklog/loaded'; events: WorklogEvent[]; cache: CacheState; contracts: string[]; latestContract: string | null; fetchedAt: number }
  | { type: 'worklog/error'; error: string }
  | { type: 'overlay/loaded'; tags: Record<string, string>; contract: string }
  | { type: 'overlay/error'; error: string }
  | { type: 'overlay/patch'; hash: string; tag: string | null }
  | { type: 'store/setComment'; itemId: string; comment: string }
  | { type: 'store/setItemState'; itemId: string; done: boolean }
  | { type: 'store/reload'; contract: string };

function reduce(state: State, action: Action): State {
  switch (action.type) {
    case 'contract/set': {
      if (action.contract === state.activeContract) return state;
      try {
        sessionStorage.setItem(ACTIVE_CONTRACT_KEY, action.contract);
      } catch {
        // ignore
      }
      return {
        ...state,
        activeContract: action.contract,
        worklog: { ...state.worklog, cache: 'loading', events: [], error: null },
        overlay: { tags: {}, contract: action.contract, error: null },
        store: loadStore(action.contract),
      };
    }
    case 'worklog/loading':
      return { ...state, worklog: { ...state.worklog, cache: 'loading', error: null } };
    case 'worklog/loaded':
      return {
        ...state,
        worklog: {
          events: action.events,
          fetchedAt: action.fetchedAt,
          cache: action.cache,
          error: null,
          contract: state.activeContract,
          contracts: action.contracts,
          latestContract: action.latestContract,
        },
      };
    case 'worklog/error':
      return {
        ...state,
        worklog: { ...state.worklog, events: [], cache: 'error', error: action.error },
      };
    case 'overlay/loaded':
      return { ...state, overlay: { tags: action.tags, contract: action.contract, error: null } };
    case 'overlay/error':
      return { ...state, overlay: { ...state.overlay, tags: {}, error: action.error } };
    case 'overlay/patch': {
      const tags = { ...state.overlay.tags };
      if (action.tag) tags[action.hash] = action.tag;
      else delete tags[action.hash];
      return { ...state, overlay: { ...state.overlay, tags } };
    }
    case 'store/setComment': {
      const comments = { ...state.store.comments, [action.itemId]: action.comment };
      const nextStore = { ...state.store, comments };
      saveStore(state.activeContract, nextStore);
      return { ...state, store: nextStore };
    }
    case 'store/setItemState': {
      const stateMap = { ...state.store.state, [action.itemId]: action.done };
      const nextStore = { ...state.store, state: stateMap };
      saveStore(state.activeContract, nextStore);
      return { ...state, store: nextStore };
    }
    case 'store/reload':
      return { ...state, store: loadStore(action.contract) };
    default:
      return state;
  }
}

const emptyWorklog: WorklogState = {
  events: [],
  fetchedAt: 0,
  cache: 'loading',
  error: null,
  contract: null,
  contracts: [],
  latestContract: null,
};

interface WorklogContextValue {
  state: State;
  setContract: (c: string) => void;
  refresh: () => Promise<void>;
  setEventTag: (hash: string, tag: string | null) => Promise<void>;
  setComment: (itemId: string, comment: string) => void;
}

const Ctx = createContext<WorklogContextValue | null>(null);

export function WorklogProvider({ children }: { children: ReactNode }) {
  // No hardcoded default week. The old fallback ('week 4') was a fossil that
  // could only ever be right for one week of the project's life; the snapshot
  // is the only thing that knows which contract is current.
  const initialContract = (() => {
    try {
      return sessionStorage.getItem(ACTIVE_CONTRACT_KEY) || FOLLOW_LIVE;
    } catch {
      return FOLLOW_LIVE;
    }
  })();

  const { snapshot } = useStatus();

  const [state, dispatch] = useReducer(reduce, undefined, (): State => ({
    activeContract: initialContract,
    worklog: emptyWorklog,
    overlay: { tags: {}, contract: null, error: null },
    store: loadStore(initialContract),
  }));

  const fetchWorklogData = useCallback(async (contract: string) => {
    try {
      const url = contract === ALL_EVENTS
        ? '/api/worklog?limit=10000'
        : '/api/worklog?contract=' + encodeURIComponent(contract) + '&limit=10000';
      const resp = await fetch(url);
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error('HTTP ' + resp.status + ' - ' + body.slice(0, 200));
      }
      const data = await resp.json();
      dispatch({
        type: 'worklog/loaded',
        events: Array.isArray(data.events) ? data.events : [],
        cache: (data.cache as CacheState) || 'unknown',
        contracts: Array.isArray(data.contracts) ? data.contracts : [],
        latestContract: data.latest_contract || null,
        fetchedAt: data.fetched_at || Date.now(),
      });
    } catch (err) {
      dispatch({ type: 'worklog/error', error: (err as Error).message });
    }
  }, []);

  const fetchOverlay = useCallback(async (contract: string) => {
    if (contract === ALL_EVENTS) {
      dispatch({ type: 'overlay/loaded', tags: {}, contract });
      return;
    }
    try {
      const resp = await fetch('/api/overlay?contract=' + encodeURIComponent(contract));
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const data = await resp.json();
      dispatch({ type: 'overlay/loaded', tags: data.tags || {}, contract });
    } catch (err) {
      dispatch({ type: 'overlay/error', error: (err as Error).message });
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!state.activeContract) return;   // still following live; nothing to fetch yet
    dispatch({ type: 'worklog/loading' });
    await Promise.all([
      fetchWorklogData(state.activeContract),
      fetchOverlay(state.activeContract),
    ]);
  }, [state.activeContract, fetchWorklogData, fetchOverlay]);

  // Refetch whenever active contract changes.
  useEffect(() => {
    void refresh();
    // refresh is recreated on contract change; that's the cue
  }, [refresh]);

  // Adopt the snapshot's current contract while unpinned. The snapshot is
  // authoritative about which week is live; the client must never decide that
  // for itself. Without this the UI could sit on a closed week indefinitely
  // even while polling perfectly good data.
  const liveContract = snapshot?.current_contract || '';
  useEffect(() => {
    if (!state.activeContract && liveContract) {
      dispatch({ type: 'contract/set', contract: liveContract });
    }
  }, [state.activeContract, liveContract]);

  const setContract = useCallback((c: string) => {
    dispatch({ type: 'contract/set', contract: c });
  }, []);

  const setEventTag = useCallback(async (hash: string, tag: string | null) => {
    const previous = state.overlay.tags[hash] ?? null;
    dispatch({ type: 'overlay/patch', hash, tag });
    try {
      const resp = await fetch('/api/overlay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contract: state.activeContract, eventHash: hash, tag: tag ?? null }),
      });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
    } catch (err) {
      dispatch({ type: 'overlay/patch', hash, tag: previous });
      alert('Tag save failed: ' + (err as Error).message + '\nReverting.');
    }
  }, [state.activeContract, state.overlay.tags]);

  const setComment = useCallback((itemId: string, comment: string) => {
    dispatch({ type: 'store/setComment', itemId, comment });
  }, []);

  return (
    <Ctx.Provider value={{ state, setContract, refresh, setEventTag, setComment }}>
      {children}
    </Ctx.Provider>
  );
}

export function useWorklog(): WorklogContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useWorklog must be inside WorklogProvider');
  return v;
}
